import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../data/data.service';
import { debounceTime, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { head, isEqual, isNil } from 'lodash';
import { UntypedFormControl } from '@angular/forms';
import { CustomerService } from '../customer/customer.service';
import { CartCustomerUser, CustomerByEmail } from '../data/models';

export interface CustomerViewModel {
  id: number;
  name: string;
  email: string;
  acceptsNewsletter: boolean;
}

const CUSTOMER_PAGE_SIZE = 5;

@Component({
    selector: 'app-cart-customer-selector',
    template: `
    <div class="modal-header">
      <h4 class="modal-title" i18n>Kunde auswählen</h4>
      <button type="button" class="btn-close" aria-label="Close" i18n-aria-label (click)="activeModal.close()"></button>
    </div>
    <div class="modal-body px-lg-4">
      <div class="input-group mb-3">
        <input class="form-control" ngbAutofocus type="search" i18n-placeholder placeholder="Kunde suchen..."
               [formControl]="searchControl" aria-describedby="button-addon-add-customer">
        <button class="btn btn-outline-secondary" type="button" id="button-addon-add-customer" (click)="openNewCustomerModal()">
          <span class="material-icons">person_add_alt_1</span>
        </button>
      </div>

      <div class="list-group" [appLoadingOverlay]="loading">
        <div *ngIf="!customers.length" class="text-muted card loader-container">
          <div class="card-body" i18n>Keinen Kunden gefunden!</div>
        </div>

        <button type="button" class="list-group-item list-group-item-action"
                [class.active]="selectedCustomer?.id === customer.id"
                *ngFor="let customer of customers" (click)="selectCustomer(customer.id)">{{customer.name}}</button>
      </div>

      <div class="d-flex justify-content-between flex-column-reverse flex-md-row pt-3">
        <button type="button" class="btn btn-lg btn-danger w-100 w-md-auto"
                (click)="removeCustomer()" i18n [disabled]="loading" [appLoadingOverlay]="loading">Löschen
        </button>

        <button class="btn btn-lg btn-info w-100 w-md-auto mb-3 mb-md-0"
                [disabled]="selectedCustomer === null"
                (click)="attachCustomer()"
                [appLoadingOverlay]="attachingCustomer"
                i18n>Kunde auswählen</button>
      </div>
    </div>
  `,
    styles: [`
    .loader-container {
      height: 191px;
    }
  `],
    standalone: false
})
export class CustomerSelectorComponent implements OnInit, OnDestroy {
  @Input() isReturn: boolean = false;
  loading = false;
  attachingCustomer = false;

  selectedCustomer: null | CustomerViewModel = null;
  customers: CustomerViewModel[] = [];
  destroy: Subject<void> = new Subject<void>();
  searchControl = new UntypedFormControl('');

  constructor(public activeModal: NgbActiveModal,
              private readonly customerService: CustomerService,
              private readonly dataService: DataService) {
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy),
      debounceTime(500),
      distinctUntilChanged(isEqual),
      map(value => !value ? null : value)
    ).subscribe(value => {
      this.searchCustomer(value);
    });
  }

  async ngOnInit() {
    await this.searchCustomer('');
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async searchCustomer(searchBy: string) {
    try {
      this.loading = true;
      this.selectedCustomer = null;
      const rawCustomers = await this.dataService.searchCustomer(searchBy);
      const filteredCustomers = rawCustomers.filter(customer => !!customer.email);
      const customerViewModels = this.toCustomerViewModel(filteredCustomers);

      const uniqueCustomers = Array.from(
        new Map(customerViewModels.map(customer => [customer.email, customer])).values()
      );

      this.customers = uniqueCustomers.slice(0, CUSTOMER_PAGE_SIZE);
    } finally {
      this.loading = false;
    }
  }

  toCustomerViewModel(list: CartCustomerUser[]): CustomerViewModel[] {
    return list.map(c => {
      const fullName = [c.firstname || '', c.lastname || ''].join(' ').trim();
      const displayedName = fullName || c.email || `${c.id}`;

      if (!c.email) {
        // at this point customers are already filtered out, so this should never happen
        throw new Error(`Customer (${c.id}) has no email`);
      }

      return {
        id: c.id,
        name: displayedName,
        acceptsNewsletter: c.acceptsNewsletter,
        email: c.email
      };
    });
  }

  async attachCustomer() {
    if (!this.selectedCustomer) {
      return;
    }

    this.attachingCustomer = true;

    try {
      await this.updateCustomerData({
        email: this.selectedCustomer.email,
        acceptsNewsletter: this.selectedCustomer.acceptsNewsletter,
      });
    } finally {
      this.attachingCustomer = false;
      this.activeModal.close();
    }
  }

  selectCustomer(id: number) {
    const customer = this.customers.find(customer => customer.id === id);
    this.selectedCustomer = isNil(customer) ? null : customer;
  }

  async openNewCustomerModal() {
    const newCustomerData = await this.customerService.openNewCustomerModal();
    if (!newCustomerData) {
      return;
    }
    const createdCustomers = await this.dataService.createNewCustomer(newCustomerData);
    const newCustomer = head(createdCustomers);

    if (!newCustomer) {
      throw new Error('After creation of customers expected to receive customers with at leas one entry.');
    }

    await this.updateCustomerData({
      email: newCustomer.email || '', // FIXME: validate instead of passing empty string
      acceptsNewsletter: newCustomer.acceptsNewsletter
    });
    this.activeModal.close();
  }

  async removeCustomer() {
    this.loading = true;
    try {
      await this.updateCustomerData(null);
      this.activeModal.close();
    } finally {
      this.loading = false;
    }
  }

  async updateCustomerData(customer: CustomerByEmail | null) {
    const action = this.isReturn ? 'updatePOSCustomer' : 'updateCartCustomer';

    await this.dataService[action](customer);
  }
}
